import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

// Informational only. There is no self-service deletion flow on the website -
// no form, no OTP, no API call. This page exists purely to describe what
// happens to an account and its data if a deletion request is made, and how
// to reach us to make one.
const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-white py-16 md:py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-4xl font-bold text-[#2C3333] mb-4 text-center tracking-tight">
          Delete Your Happy Tring Account
        </h1>
        <p className="text-[#4B5563] text-center max-w-xl mx-auto mb-10 leading-relaxed">
          This page explains what happens to your Happy Tring account and personal data if you request deletion. There is no self-service deletion here - to request deletion, please contact us using the details below.
        </p>

        <div className="flex items-start gap-3 bg-destructive/5 border border-destructive/30 rounded-xl p-4 mb-10">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-[#4B5563]">
            <strong className="text-[#2C3333]">Deletion is permanent.</strong> Once a deletion request is processed, your account cannot be recovered.
          </p>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="font-heading text-xl font-semibold text-[#2C3333] border-b pb-2 mb-4">What will be deleted</h2>
            <ul className="space-y-2 text-[#4B5563] list-disc list-inside">
              <li>Your account and profile record</li>
              <li>Your wallet balance</li>
              <li>Your saved favourites</li>
              <li>Your OTP history</li>
              <li>If your number belongs to a counsellor: your provider profile, everyone's favourites pointing at you, and any pending rate-change request</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold text-[#2C3333] border-b pb-2 mb-4">Data that may be retained</h2>
            <p className="text-[#4B5563] leading-relaxed">
              We keep a minimal internal log of the deletion itself - your phone number, account type, the reason you provided (if any), and a timestamp - kept for fraud and abuse investigation, and not linked to any other personal data since the rest of your account is deleted. Call records, transactions and ratings are retained as financial and audit history, as required for accounting and dispute resolution. Please refer to our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for more details on our data retention practices.
            </p>
          </section>

          <section className="bg-secondary/10 p-6 rounded-lg border border-border/40">
            <h2 className="font-heading text-lg font-semibold text-[#2C3333] mb-2">How to request deletion</h2>
            <p className="text-[#4B5563]">
              To request deletion of your account and data, please email us at{' '}
              <a href="mailto:happytringdpl@gmail.com" className="text-primary font-medium hover:underline">happytringdpl@gmail.com</a>{' '}
              from your registered contact, and we will verify your identity and process the request.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
